import React from 'react';
import PropTypes from 'prop-types';
import { BAND_FILL } from './verdictModel';

const NAVY = '#14536e';

// Reading guide + colour key. The key changes with the metric, because the colour
// channel means different things:
//   • Overall → the gated band (4 discrete verdicts: Keep/Polish/Rework/two-lenses-low)
//   • Design & Execution → the legacy look (Design border + Execution fill, gradient)
//   • any single axis/lens → that axis's 0–10 score (gradient)
//
// The worst stop used to read "Cut / rebuild". This legend colours EVERY ring — script, act,
// sequence and scene — and at scene/sequence grain the unit was graded in isolation, so a cut or
// merge is a claim the evidence cannot support (see verdictModel.bandLabel and
// DER_SCENE_TRIAGE_AUDIT_v0_1.md). One label has to hold at all four grains, so it names the
// evidence — two or more of Design/Execution/Read came out low — not an action.
// 'partial' is the fifth stop and deliberately not on the good→bad run: it means the unit is
// missing at least one of Design/Execution/Read, so the gate cannot assert the absence of a broken
// lens and declines to call it. Listed here because the previous behaviour — silently returning a
// score gradient with no legend entry — was the unlabelled state this legend exists to prevent.
// (verdictModel.gatedBand, landmine 76.)
const BAND_KEY = [
  ['keep', 'Keep'], ['polish', 'Polish'], ['rework', 'Rework'], ['heavy', 'Two groups low'],
  ['partial', 'Not fully read'],
];

// surface: 'wheel' (SoS sunburst, default) shows the clockwise reading-order hint;
// 'timeline' (icicle) suppresses it — that page reads left→right and says so under the grid.
export default function SunburstLegend({ metric = 'overall', onSwitchToTable, surface = 'wheel' }) {
  const isOverall = metric === 'overall';
  const isDE = metric === 'de';
  return (
    <div className="px-3 pb-2 pt-1" id="sos-legend" style={{ fontSize: 12, color: '#48606c' }}>
      <div className="d-flex align-items-center flex-wrap" style={{ gap: '6px 18px' }}>
        {surface === 'wheel' && (
          <span style={{ color: NAVY }}>
            <span aria-hidden style={{ fontSize: 14 }}>↻</span> Reading order: clockwise from the top.
          </span>
        )}

        {/* Overall = the gated band: 4 discrete verdict colours */}
        {isOverall && BAND_KEY.map(([k, label]) => (
          <span key={k} className="d-inline-flex align-items-center" style={{ gap: 6 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: BAND_FILL[k] }} />
            <strong style={{ color: NAVY }}>{label}</strong>
          </span>
        ))}

        {/* Design & Execution: on the wheel the channels are border/fill; on the timeline each
            cell SPLITS — top half Design, bottom half Execution (far more legible). */}
        {isDE && surface === 'timeline' && (
          <span className="d-inline-flex align-items-center" style={{ gap: 6 }}>
            <span style={{ width: 15, height: 15, borderRadius: 3, border: '1px solid #b9c8d0',
              background: 'linear-gradient(180deg,#5BB04A 0%,#5BB04A 50%,#D93030 50%,#D93030 100%)' }} />
            top = <strong style={{ color: NAVY }}>Design</strong> · bottom = <strong style={{ color: NAVY }}>Execution</strong>
          </span>
        )}
        {isDE && surface !== 'timeline' && (
          <>
            <span className="d-inline-flex align-items-center" style={{ gap: 6 }}>
              <span style={{ width: 15, height: 15, borderRadius: 3, background: '#e8eef1', border: `2.5px solid ${NAVY}` }} />
              border = <strong style={{ color: NAVY }}>Design</strong>
            </span>
            <span className="d-inline-flex align-items-center" style={{ gap: 6 }}>
              <span style={{ width: 15, height: 15, borderRadius: 3, background: NAVY, border: '1px solid #b9c8d0' }} />
              fill = <strong style={{ color: NAVY }}>Execution</strong>
            </span>
          </>
        )}

        {/* score gradient — shown for D&E and single-axis reads (not the discrete band) */}
        {!isOverall && (
          <span className="d-inline-flex align-items-center" style={{ gap: 6 }}>
            <span style={{ display: 'inline-block', width: 56, height: 9, borderRadius: 5,
              background: 'linear-gradient(90deg,#E25C53 0%,#F9C8AB 50%,#5DB85F 100%)' }} />
            needs work → strong
          </span>
        )}

        {/* not-owed key — only single axis/lens reads can be "not owed" in a unit */}
        {!isOverall && !isDE && (
          <span className="d-inline-flex align-items-center" style={{ gap: 6 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: '#eef2f5', border: '1px solid #dbe3e8' }} />
            grey = <strong style={{ color: NAVY }}>not owed here</strong>
          </span>
        )}

        {onSwitchToTable && (
          <button type="button" onClick={onSwitchToTable}
            className="btn btn-link btn-sm p-0 text-decoration-none ms-auto"
            style={{ color: NAVY, fontWeight: 600, fontSize: 12 }}>
            Prefer a list? See the table →
          </button>
        )}
      </div>
    </div>
  );
}

SunburstLegend.propTypes = {
  metric: PropTypes.string,
  onSwitchToTable: PropTypes.func,
};
